import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
} from "@mui/material";
import { Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import Header from "../../../Header1";
import log from "../../../../utils/log";
import { API_URL } from "../../../../api";
import { tokens } from "../../../../theme";
import Loader from "../../../Utility/Loader";

import UserAvatar from "../UserAvatar";
import { formatValue } from "../../../../utils/utils";
import UserProfileDetails from "../UserProfileDetails";
import * as adminUserAction from "../../../../actions/admin/user";
import { approvalStatuses, reasons } from "../../../../utils/constants";
import { getProfileSelfieImageUrls } from "../../../../utils/serverUtils";
import ConfirmationModal from "../../../Utility/ConfirmationModal";


const RejectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [details, setDetails] = useState(null);
  const [update, setUpdate] = useState(false);
  const [loading, setLoading] = useState(false);

  const [reason, setReason] = useState("");
  const [quesAns, setQuesAns] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [customReason, setCustomReason] = useState("");
  const [selectedReason, setSelectedReason] = useState("");
  const [RejectReason, setRejectReason] = useState({});
  const location = useLocation();

  const [modalConfig, setModalConfig] = useState({
    show: false,
    title: "",
    message: "",
    confirmLabel: "",
    onConfirm: () => { },
  });

  const handleSelectChange = (event) => {
    const selected = event.target.value;
    setSelectedReason(selected);
    setCustomReason("");
    setReason(selected);
  };

  const handleCustomReasonChange = (event) => {
    const customText = event.target.value;
    setCustomReason(customText);
    setSelectedReason("");
    setReason(customText);
  };

  const [images, setImages] = useState({
    main: null,
    first: null,
    second: null,
  });

  const ImageURL = async () => {
    try {
      const response = await adminUserAction.getUserMedias(id);
      if (response) {
        const profileImages = getProfileSelfieImageUrls(response);
        setImages(profileImages);
      }
    } catch (error) {
      log.error("Error saving images:", error);
    }
  };

  const detailsfetchData = async () => {
    try {
      setLoading(true);
      const response = await adminUserAction.getCustomerByID(id);

      // Clone the object before mutating
      const sanitizedDetails = { ...response };

      // Remove unwanted keys
      [
        "id",
        "user_id",
        "completed",
        "email_verified_at",
        "birthday",
        "gender",
        "want_gender",
        "created_at",
        "updated_at",
        "popup_promo_shown_stage",
      ].forEach((key) => delete sanitizedDetails[key]);

      setDetails(sanitizedDetails);
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const rejectreasionfetch = async () => {
    try {
      const response = await fetch(
        `${API_URL}admin/rejected/latestrejection/${id}`
      );
      const data = await response.json();
      setRejectReason(data);
    } catch (err) { }
  };

  const quesAnsfetchData = async () => {
    try {
      const response = await fetch(
        `${API_URL}admin/users/user/questions/${id}`
      );

      if (!response.ok) {
        if (response.status === 404) {
          log.error("Resource not found");
          setQuesAns([]);
          return;
        }
        throw new Error(`Failed to fetch questions: ${response.statusText}`);
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        setQuesAns([]);
      } else {
        setQuesAns(data);
      }
    } catch (err) {
      log.error("Error fetching questions:", err);
      setQuesAns([]);
    }
  };

  const GetUserFromUpdateMedia = useCallback(async () => {
    try {
      const response = await adminUserAction.getUserUpdateRequestedUser(id);
      setUpdate(response?.length !== 0);
    } catch (err) {
      log.error("Internal Server Error:", err);
    }
  }, [id]);

  useEffect(() => {
    quesAnsfetchData();
    detailsfetchData();
    GetUserFromUpdateMedia();
    ImageURL();
    rejectreasionfetch();
  }, [id]);

  const updateStatusReject = async (newStatus) => {
    try {
      const response = await fetch(`${API_URL}admin/users/updatestatus`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: id, approval: newStatus, message: reason }),
      });

      if (!response.ok) {
        const errorDetails = await response.text(); // Get error details for debugging
        throw new Error(`Failed to update status: ${errorDetails}`);
      }

      // Fetch updated details after updating the status
      await detailsfetchData();
      handleCloseModal();
      navigate("user/list");
    } catch (err) {
      log.error(err);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setReason("");
  };

  const handleSaveReason = () => {
    updateStatusReject(30);
  };

  // Delete User
  const handleDeleteRequestConfirmation = async () => {
    setModalConfig({
      show: true,
      onHide: () => setModalConfig((prev) => ({ ...prev, show: false })),
      title: "Delete User",
      message: "Are you sure you want to delete this user?",
      confirmLabel: "Delete",
      onConfirm: handleDeleteRequest,
    });
  };
  const handleDeleteRequest = async () => {
    try {
      setLoading(true);
      await adminUserAction.deleteUser(
        JSON.stringify({
          id: id,
        })
      );
      setLoading(false);
      handleNavigate();
    } catch (err) {
      console.error(err);
    }
  };
  const handleNavigate = () => {
    const path = location.pathname;

    if (path.startsWith("/admin/user/approval/")) {
      navigate("/admin/user/approval");
    } else {
      navigate("/admin/user/list");
    }
  };

  if (!details) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <div className="container">
      {loading && <Loader />}
      <ConfirmationModal {...modalConfig} />
      <Header title="User Details" />
      <div className="row g-4">
        {/* Left side: Avatar + Name */}
        <div className="col-12 col-md-4">
          <div className="d-flex flex-column align-items-center my-3">
            <div className="mb-3 w-100">
              <UserAvatar images={images} />
            </div>
            <h5 className="text-center">
              {`${formatValue("name", details.first_name)} ${formatValue(
                "surname",
                details.last_name
              )}`}
            </h5>
            <p className="text-muted text-center">
              {formatValue(
                "status",
                approvalStatuses[details.approval] || "Unknown Status"
              )}
            </p>
          </div>
        </div>

        {/* Right side: Details + Q&A + Reject Reason */}
        <div className="col-12 col-md-8">
          <div className="row g-3">
            {/* User Profile Details */}
            <div className="col-12">
              <UserProfileDetails details={details} />
            </div>

            {/* Question & Answer Section */}
            {quesAns?.length > 0 && (
              <div className="col-12 mb-4">
                <h4 className="mb-3">Question & Answers</h4>
                {quesAns.map((item, index) => (
                  <div key={index} className="mb-3">
                    <h6 className="fw-bold">{item.question}</h6>
                    <p className="mb-0">{item.answer}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Reject Reason */}
            <div className="col-12 mb-4">
              <h4 className="mb-3" style={{ color: colors.primary[500] }}>
                Reject Reason
              </h4>
              <h6 className="fw-bold">{RejectReason.reason}</h6>
            </div>
          </div>

          {/* Footer Button Section */}
          <div className="row g-3 mb-3">
            <div className="d-flex flex-column align-items-center justify-content-center gap-3 mt-4">
              <div className="row gy-2 justify-content-center">
                <div className="hstack gap-2 justify-content-sm-end">
                  <div className="text-center">
                    <button
                      className="btn btn-danger"
                      onClick={() => handleDeleteRequestConfirmation()}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            width: "360px",
            height: "400px",
          },
        }}
      >
        <DialogTitle>What's the reason?</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense" variant="outlined">
            <InputLabel>Select a reason</InputLabel>
            <Select
              value={selectedReason}
              onChange={handleSelectChange}
              label="Select a reason"
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                    width: "calc(360px - 32px)",
                    whiteSpace: "normal",
                  },
                },
              }}
            >
              {reasons.map((reason, index) => (
                <MenuItem
                  key={index}
                  value={reason}
                  sx={{ whiteSpace: "normal", wordWrap: "break-word" }}
                >
                  {reason}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            autoFocus
            margin="dense"
            label="Reason"
            type="text"
            fullWidth
            variant="outlined"
            value={customReason}
            onChange={handleCustomReasonChange}
            multiline
            rows={5}
            sx={{
              marginTop: "16px",
              "& .MuiInputBase-root": {
                height: "150px",
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} variant="contained" color="error">
            Cancel
          </Button>
          <Button
            onClick={() => handleSaveReason(reason)}
            sx={{
              background: "linear-gradient(90deg, #FC8C66, #F76A7B)",
              color: "#fff",
              "&:hover": {
                background: "linear-gradient(90deg, #FC8C66, #F76A7B)",
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RejectDetails;
